/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Component } from 'react'
import { OrderStatus } from '../../models/order';
import { TableList, TableListHeader } from '../../_shared/table-list';
import { Icon, Input, Checkbox, Modal, message } from 'antd';
import Row from '../../_shared/row';
import SStyles from '../../_shared/styles';
import { get } from 'lodash';
import { getRoutes, createRoute } from '../../_shared/services/routes.service';
import { withRouter } from 'react-router-dom';
import { searchTable, sortByName, userHasRole } from '../../actions/utils';
import { Button } from '../../_shared/button';

const COLS = [
  {
    title: 'Route Name',
    dataIndex: 'name',
  },
  {
    title: 'Locations',
    dataIndex: 'data.rows',
    // The cell shows a count, which nobody searches for. The thing people actually look a
    // route up by is the place it serves, and that is what this column stands for, so its
    // searchable text is the cities and zips behind the number as well as the number.
    searchValue: route => {
      const rows = get(route, 'data.rows') || [];
      return [rows.length, ...rows.map(r => `${get(r, 'city', '')} ${get(r, 'zipCode', '')}`)].join(' ');
    },
    render: (text) => {
      return (
        <span>{(text || []).length}</span>
      )
    }
  },
]

export class RoutesComponent extends Component {
  state = {
    allRoutes: [],
    routes: [],
    loading: false,
    error: undefined,
    success: undefined,
    isAdmin: false, //whether to setup admin funcitonalitiy
    selectedRowKeys: [],
    copyModalVisible: false,
    copyNewName: '',
  }
  componentDidMount = () => {
    this.getRoutes();
  }
  getRoutes = async () => {
    try {
      const { sState } = this.props;
      const { location } = this.props.history;
      let isAdmin = false;
      if (location.pathname.indexOf('manage') > -1 && userHasRole([0, 1], sState)) {
        isAdmin = true;
      }

      this.setState({ loading: true });
      const routes = (await getRoutes()).sort(sortByName);
      this.setState({ allRoutes: routes, routes, loading: false, isAdmin });
    } catch (err) {
      this.setState({ loading: false, error: err.message });
    }
  }
  // Card #277 — this used to match pickupCompany/destCompany/orderId/orderType, none of
  // which exist on a route: it was copied from the orders screen, so every search on this
  // page returned nothing at all. It now searches the columns the grid actually renders.
  filterRoutes = (searchTerm = '') => {
    const { allRoutes } = this.state;
    const routes = searchTable(COLS, allRoutes, searchTerm);
    this.setState({ routes });
  }
  selectRoute = (record, index) => {
    this.props.history.push('/dashboard/editRoute', {
      editRoute: record,
    })
  }
  onSearch = (st) => {
    this.filterRoutes(st);
  }
  filterDelivered = (deliveredOnly) => {
    const { allRoutes } = this.state;
    const routes = deliveredOnly ? allRoutes.filter(o => get(o, 'data.order.status', 0) >= OrderStatus.delivered) : allRoutes;
    this.setState({ routes });
  }
  onCheckChange = (e) => {
    const checked = e.target.checked;
    this.filterDelivered(checked);
  }
  onSelectedRowsChange = (keys) => {
    this.setState({ selectedRowKeys: keys });
  }
  openCopyModal = () => {
    const selectedRoute = this.state.routes.find(r => r.id === this.state.selectedRowKeys[0]);
    this.setState({ copyModalVisible: true, copyNewName: get(selectedRoute, 'name', '') + ' (Copy)' });
  }
  closeCopyModal = () => {
    this.setState({ copyModalVisible: false, copyNewName: '' });
  }
  handleCopyRoute = async () => {
    const { selectedRowKeys, copyNewName, routes } = this.state;
    if (!copyNewName.trim()) return;
    try {
      this.setState({ loading: true });
      const sourceRoute = routes.find(r => r.id === selectedRowKeys[0]);
      const sourceRows = get(sourceRoute, 'data.rows', []);
      const newRoute = await createRoute({ name: copyNewName.trim(), data: { rows: [...sourceRows] } });
      message.success(`Route "${copyNewName.trim()}" created with ${sourceRows.length} locations`);
      this.closeCopyModal();
      this.getRoutes();
    } catch (err) {
      message.error(err.message || 'Failed to copy route');
    } finally {
      this.setState({ loading: false });
    }
  }
  createRoute = async () => {
    try {
      this.setState({ loading: true });
      const route = await createRoute({ name: '', data: { rows: [] } });
      this.props.history.push('/dashboard/editRoute', {
        editRoute: route,
      })
      this.setState({ loading: false });
    } catch (err) {
      console.error(err);
      this.setState({ loading: false });
    }
  }
  render() {
    const {
      loading,
      routes,
      selectedRowKeys,
      copyModalVisible,
      copyNewName,
    } = this.state;

    const hasOneSelected = selectedRowKeys.length === 1;
    const selectedRoute = hasOneSelected ? routes.find(r => r.id === selectedRowKeys[0]) : null;

    return (
      <div>
        <TableListHeader>
          <Row css={css(`width: 1000px;`)}>
            <Input.Search
              placeholder={'Search all columns...'}
              enterButton
              onSearch={this.onSearch}
            />
            <Checkbox onChange={this.onCheckChange} css={css(SStyles.row, `margin-left: 10px;`)}>
              Show Only Delivered
            </Checkbox>
          </Row>
          <Row>
            {hasOneSelected && (
              <Button loading={loading} onClick={this.openCopyModal} style={{ marginRight: 8 }}>
                <Icon type="copy" />
                {'Copy Route'}
              </Button>
            )}
            <Button loading={loading} onClick={() => this.createRoute()}>
              <Icon type="plus" />
              {'Add Route'}
            </Button>
          </Row>
        </TableListHeader>
        <TableList
          loading={loading}
          data={routes}
          columns={COLS}
          rowClick={this.selectRoute}
          onRefresh={this.getRoutes}
          canSelectRows={true}
          onSelectedRowsChange={this.onSelectedRowsChange}
          selectedRowKeys={selectedRowKeys}
        />
        <Modal
          title={`Copy Route: ${get(selectedRoute, 'name', '')}`}
          visible={copyModalVisible}
          onOk={this.handleCopyRoute}
          onCancel={this.closeCopyModal}
          okText="Copy"
          okButtonProps={{ disabled: !copyNewName.trim() }}
          confirmLoading={loading}
        >
          <p>Create a new route with all {get(selectedRoute, 'data.rows.length', 0)} locations from <strong>{get(selectedRoute, 'name', '')}</strong>.</p>
          <Input
            placeholder="New route name"
            value={copyNewName}
            onChange={e => this.setState({ copyNewName: e.target.value })}
            onPressEnter={this.handleCopyRoute}
          />
        </Modal>
      </div>
    )
  }
}

export const Routes = withRouter(RoutesComponent);
